import React, { useState, useRef } from 'react';
import uploadIcon from '../../static/create_character/upload-icon.svg';
import Dropdown from '../common/Dropdown';

interface CharacterFormProps {
  onSubmit: (characterData: FormData, callback: () => void) => void;
  onBack: () => void;
}

const CharacterForm: React.FC<CharacterFormProps> = ({ onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    additional_details: '',
    image: null as File | null
  });
  const [imageError, setImageError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDropdownChange = (fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) { // 20MB in bytes
        setImageError('Image size must be less than 20MB.');
        return;
      }
      setImageError('');
      setFormData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    const submitData = new FormData();
    
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== '') {
        submitData.append(key, value as string);
      }
    });

    const resetSubmitting = () => {
      setIsSubmitting(false);
    };

    onSubmit(submitData, resetSubmitting);
  };

  const genderOptions = [
    { value: 'Female', label: 'Female' },
    { value: 'Male', label: 'Male' },
    { value: 'Other', label: 'Other' }
  ];

  return (
    <>
      <div className="dialog-header">
        <h2>Add character</h2>
        <button className="close-btn" onClick={onBack}></button>
      </div>

      <div className="character-form" style={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '60vh',
        padding: '20px 0'
      }}>
        <div className="form-content" style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center',
          flex: '1',
          width: '100%'
        }}>
          <div className="form-group" style={{ marginBottom: '24px' }}>
            <div className="self-stretch justify-start text-neutral-500 text-base font-semibold font-['Montserrat'] leading-normal">Name</div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter name"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '24px' }}>
            <div className="self-stretch justify-start text-neutral-500 text-base font-semibold font-['Montserrat'] leading-normal">Age</div>
            <input
              type="number"
              name="age"
              value={formData.age}
              onChange={handleInputChange}
              placeholder="Enter age"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '24px' }}>
            <div className="self-stretch justify-start text-neutral-500 text-base font-semibold font-['Montserrat'] leading-normal">Gender</div>
            <Dropdown
              name="gender"
              value={formData.gender}
              options={genderOptions}
              placeholder="-- Select gender --"
              onChange={handleDropdownChange}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '24px' }}>
            <div className="self-stretch justify-start text-neutral-500 text-base font-semibold font-['Montserrat'] leading-normal">Additional Details</div>
            <textarea
              name="additional_details"
              value={formData.additional_details}
              onChange={handleInputChange}
              placeholder="Enter additional details"
              rows={4}
            />
          </div>

          <div className="form-group">
            <div className="self-stretch justify-start text-neutral-500 text-base font-semibold font-['Montserrat'] leading-normal">Image</div>
            <div className="image-upload-container">
              {formData.image ? (
                <div className="image-preview-container">
                  <img 
                    src={URL.createObjectURL(formData.image)} 
                    alt="Preview" 
                    className="image-preview"
                  />
                  <button 
                    className="remove-image-button"
                    onClick={() => setFormData(prev => ({...prev, image: null}))}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="image-upload-content" onClick={handleImageClick}>
                  <div className="image-upload-icon-wrapper">
                    <img src={uploadIcon} alt="Upload" />
                  </div>
                  <div className="image-upload-text">Add Character Image</div>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="image-upload-input"
                ref={fileInputRef}
              />
            </div>
            {imageError && (
              <div style={{ color: 'red', marginTop: '8px', fontSize: '14px' }}>{imageError}</div>
            )}
          </div>
        </div>

        <div className="form-button-container" style={{ 
          marginTop: '40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button 
            className="back-to-list"
            onClick={onBack}
            style={{
              padding: '14px 32px',
              minWidth: '220px',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '16px',
              fontWeight: 600
            }}
          >
            ← Back to list
          </button>
          <button 
            className="next-btn"
            onClick={handleSubmit}
            disabled={!formData.name || !formData.image || isSubmitting}
            style={{
              width: 'auto',
              minWidth: '220px',
              padding: '14px 32px'
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Character'}
          </button>
        </div>
      </div>
    </>
  );
};

export default CharacterForm;